import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import { US_STATES, CA_CITIES } from "@/lib/formHelpers";
import { Play, Trash2, Plus, Clock, ExternalLink, PhoneCall } from "lucide-react";

const INDUSTRIES = ["Plumber", "Lawyer", "Dentist", "HVAC", "Insurance",
  "Electrician", "Roofer", "Landscaper", "Auto Repair", "Chiropractor"];

export default function Watchlists() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ industry: "Plumber", city: "Los Angeles", state: "CA", source: "both", max_per_run: 20, active: true });
  const [creating, setCreating] = useState(false);
  const [runningId, setRunningId] = useState(null);
  const [callingId, setCallingId] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get("/watchlists");
      setRows(data.watchlists || []);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await api.post("/watchlists", form);
      toast.success("Watchlist added");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCreating(false); }
  };

  const runOne = async (id) => {
    setRunningId(id);
    try {
      const { data } = await api.post(`/watchlists/${id}/run`);
      toast.success(`Imported ${data.imported} new leads (${data.found_total} found · ${data.already_existing} already in pipeline)`);
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setRunningId(null); }
  };

  const runAndCall = async (id) => {
    if (!window.confirm("This will discover new leads AND immediately fire AI phone calls to every fresh lead with a phone number (up to 25). This uses Vapi credits. Continue?")) return;
    setCallingId(id);
    try {
      const { data } = await api.post(`/watchlists/${id}/run-and-call`);
      const c = data.calling || {};
      toast.success(
        `Imported ${data.imported} new leads · AI calls queued: ${c.queued || 0}${c.skipped ? ` · skipped ${c.skipped}` : ""}${c.error ? ` · error: ${c.error}` : ""}`
      );
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setCallingId(null); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this watchlist?")) return;
    try {
      await api.delete(`/watchlists/${id}`);
      toast.success("Watchlist deleted");
      await load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const totalImported = useMemo(() => rows.reduce((s, r) => s + (r.total_imported || 0), 0), [rows]);

  return (
    <div className="p-10" data-testid="watchlists-page">
      <Toaster position="top-right" richColors />
      <div className="flex items-start justify-between mb-8">
        <div>
          <div className="overline mb-2">Lead engine</div>
          <h1 className="font-display text-4xl font-black tracking-tight">Watchlists</h1>
          <p className="text-neutral-600 mt-2 max-w-2xl">
            Save industry + city combos and we&apos;ll auto-discover new no-website businesses every night at 3 AM UTC. Zero manual work.
          </p>
        </div>
        <div className="text-right">
          <div className="overline">Auto-imported total</div>
          <div className="font-display text-3xl font-bold text-[#002FA7]">{totalImported}</div>
        </div>
      </div>

      {/* Create form */}
      <form onSubmit={create} className="bg-white border border-neutral-200 p-6 grid grid-cols-1 md:grid-cols-6 gap-3 items-end mb-8">
        <div className="md:col-span-1">
          <label className="overline block mb-2">Industry</label>
          <select value={form.industry} onChange={(e) => setForm({ ...form, industry: e.target.value })} className="lf-input">
            {INDUSTRIES.map(i => <option key={i}>{i}</option>)}
          </select>
        </div>
        <div className="md:col-span-2">
          <label className="overline block mb-2">City</label>
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className="lf-input"
            list={form.state === "CA" ? "wl-ca-cities" : undefined} required />
          {form.state === "CA" && <datalist id="wl-ca-cities">{CA_CITIES.map(c => <option key={c} value={c} />)}</datalist>}
        </div>
        <div className="md:col-span-1">
          <label className="overline block mb-2">State</label>
          <select value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className="lf-input" required>
            {US_STATES.map(([c, n]) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="md:col-span-1">
          <label className="overline block mb-2">Source</label>
          <select value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className="lf-input">
            <option value="both">Both</option>
            <option value="google">Google only</option>
            <option value="osm">OpenStreetMap only</option>
          </select>
        </div>
        <button disabled={creating} className="lf-btn-primary flex items-center justify-center gap-2 md:col-span-1" data-testid="watchlist-create">
          <Plus className="w-4 h-4" /> {creating ? "Adding…" : "Add"}
        </button>
      </form>

      {/* Table */}
      {loading ? <div>Loading…</div> : rows.length === 0 ? (
        <div className="bg-white border border-neutral-200 p-10 text-center text-neutral-500">
          No watchlists yet. Add one above to start auto-discovery.
        </div>
      ) : (
        <div className="bg-white border border-neutral-200">
          <table className="lf-table w-full">
            <thead><tr>
              <th>Industry</th><th>Location</th><th>Source</th>
              <th>Total imported</th><th>Last run</th><th></th>
            </tr></thead>
            <tbody>
              {rows.map(w => (
                <tr key={w.id} data-testid={`watchlist-row-${w.id}`}>
                  <td className="font-semibold">{w.industry}</td>
                  <td>{w.city}, {w.state}</td>
                  <td><span className="text-xs font-mono bg-neutral-100 px-2 py-0.5 border border-neutral-200">{w.source}</span></td>
                  <td className="font-mono">{w.total_imported || 0}</td>
                  <td className="text-xs text-neutral-500 font-mono">
                    {w.last_run_at ? new Date(w.last_run_at).toLocaleString() : <span className="text-amber-700"><Clock className="w-3 h-3 inline" /> pending</span>}
                  </td>
                  <td className="text-right">
                    <button onClick={() => runOne(w.id)} disabled={runningId === w.id || callingId === w.id} data-testid={`watchlist-run-${w.id}`}
                            className="lf-btn-outline text-xs mr-2 inline-flex items-center gap-1 px-2 py-1">
                      <Play className="w-3 h-3" /> {runningId === w.id ? "Running…" : "Run now"}
                    </button>
                    <button onClick={() => runAndCall(w.id)} disabled={runningId === w.id || callingId === w.id} data-testid={`watchlist-run-and-call-${w.id}`}
                            className="text-xs mr-2 inline-flex items-center gap-1 px-2 py-1 bg-[#E24E1B] text-white hover:bg-[#0F2340] transition disabled:opacity-50">
                      <PhoneCall className="w-3 h-3" /> {callingId === w.id ? "Calling…" : "Run + AI Call"}
                    </button>
                    <button onClick={() => remove(w.id)} data-testid={`watchlist-delete-${w.id}`}
                            className="text-red-600 hover:text-red-800 p-1">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-6 text-xs text-neutral-500 flex items-center gap-2">
        <Clock className="w-3 h-3" /> Nightly cron runs at 3:00 AM UTC daily and executes every active watchlist automatically.
      </div>
    </div>
  );
}
